import BioApi from '@/api/bio';
import { confirmDialog } from '@/components/dialog';
import { IndentityScoreEnum } from '@/mixins/finance-identity/types';
import { OcrProcessSourceEnum } from '@/shared/tools/ocr/enum';
import { TC_OCR_NOT_CLEAR_POPUP } from '@/shared/tools/ocr/tianqi';
import { ubtSendError } from '@/shared/utils/ubt';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import jumpToVerifyCredit from '@/shared/verify-credit-controller';
import { VerifySceneEnum } from '@/shared/verify-credit-controller/types';

interface IdentityScoreCache {
  shouldUpdateIdentityPhoto: boolean;
  updateIdentityMessage: string;
}

class IdentityScoreService {
  private initIdentityScoreTaskCache: Promise<IdentityScoreCache> | null = null;

  /** 初始化身份证清晰分 */
  private async initIdentityScoreData(scene?: OcrProcessSourceEnum): Promise<IdentityScoreCache> {
    if (!this.initIdentityScoreTaskCache) {
      this.initIdentityScoreTaskCache = new Promise(async (resolve, reject) => {
        try {
          const identityScoreBizeData = await BioApi.updatedIdentity({
            scene,
          });

          resolve({
            shouldUpdateIdentityPhoto: [
              IndentityScoreEnum.sateBizCodeTypeHua,
              IndentityScoreEnum.sateBizCodeTypeTecent,
            ].includes(identityScoreBizeData.bizCode),
            updateIdentityMessage: identityScoreBizeData.message
              || '您的身份证信息已失效，请重新上传您的身份证正反面照片',
          });
        } catch (error) {
          reject(error);
        }
      });
    }

    return this.initIdentityScoreTaskCache;
  }

  /**
   * 确认用户上传的身份证清晰分是否达标
   * @param scene ocr接口请求场景
   * @param notClearCallback 身份证清晰分不足的回调方法，默认不传
   * @returns 是否需要更新身份证照片
   */
  public async confirmIdentityScoreClarity(
    scene?: OcrProcessSourceEnum,
    notClearCallback?: () => void,
  ): Promise<boolean> {
    try {
      const {
        shouldUpdateIdentityPhoto,
        updateIdentityMessage,
      } = await this.initIdentityScoreData(scene);

      if (shouldUpdateIdentityPhoto) {
        if (notClearCallback) {
          notClearCallback();
        }

        ubtSetupModuleExpose(TC_OCR_NOT_CLEAR_POPUP);

        try {
          /** 确认弹窗：是否进入更新身份证流程 */
          await confirmDialog.open({
            title: '身份证更新',
            content: updateIdentityMessage,
            confirmText: '我知道了',
          });

          /** 清空缓存数据，避免更新不及时问题 */
          this.initIdentityScoreTaskCache = null;

          /** 进入资料补充页：OCR上传 && 授信流程 */
          jumpToVerifyCredit(VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY);

          ubtSetupPageButton(TC_OCR_NOT_CLEAR_POPUP, {
            buttonType: '1', // 按钮：我知道了
          });
        } catch (error) {
          ubtSetupPageButton(TC_OCR_NOT_CLEAR_POPUP, {
            buttonType: '0', // 按钮：关闭
          });
        }

        return false;
      }
    } catch (error) {
      /** 若是接口异常，默认返回 true，避免阻塞用户正常流程 */
      ubtSendError(error as Error);
    }

    return true;
  }

  /** 提前预加载身份证清晰分检测数据 */
  public async preloadIdentityScoreData(): Promise<void> {
    try {
      /** 清空缓存数据，更新不及时问题 */
      this.initIdentityScoreTaskCache = null;
      await this.initIdentityScoreData();
    } catch (error) {
      ubtSendError({
        name: 'preloadIdentityScoreData error',
        message: (error as Error)?.message,
      });
    }
  }
}

/** 身份证清晰分不足服务检测服务-实例 */
export const identityScoreService = new IdentityScoreService();
